import { ApolloClient, createHttpLink, from, InMemoryCache, split } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { DateTime } from "luxon";
import type { IncomingAppointmentConnection } from "@/gql/graphql";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { ConnectionData, mergeConnections } from "./cursor-pagination-merge";
import { authContextLink } from "./links/auth-context-link";
import { localizedAuthErrorLink } from "./links/localized-auth-error-link";
import { tokenRefreshLink } from "./links/token-refresh-link";

// HTTP link
const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL || "http://localhost:3200/graphql",
});

// WebSocket connection state tracking
let wsConnectionState: "disconnected" | "connecting" | "connected" | "error" = "disconnected";
let wsReconnectAttempts = 0;
const maxReconnectAttempts = 5;

// WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.VITE_GRAPHQL_WS_URL || "ws://localhost:3200/graphql",
    connectionParams: () => {
      const token = useAuthStore.getState().accessToken;
      return {
        authorization: token ? `Bearer ${token}` : "",
      };
    },
    retryAttempts: maxReconnectAttempts,
    retryWait: async (retryCount) => {
      const waitTime = Math.min(1000 * 2 ** retryCount, 30000); // Exponential backoff, max 30 seconds
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    },
    shouldRetry: (errOrCloseEvent) => {
      // Don't retry authentication errors or connection rejections
      if (errOrCloseEvent && typeof errOrCloseEvent === "object") {
        // Check for connection rejection (4403 = Forbidden)
        if ("code" in errOrCloseEvent && errOrCloseEvent.code === 4403) {
          wsConnectionState = "error";
          console.error("[WebSocket] Authentication failed - not retrying");
          return false;
        }
        // Fallback to message checking for other auth-related errors
        if ("message" in errOrCloseEvent) {
          const rawMessage = errOrCloseEvent.message;
          const message = (typeof rawMessage === "string" ? rawMessage : "").toLowerCase();
          if (
            message.includes("authorization") ||
            message.includes("authentication") ||
            message.includes("unauthorized")
          ) {
            wsConnectionState = "error";
            console.error("[WebSocket] Authorization error - not retrying");
            return false;
          }
        }
      }

      // Retry for other errors if under max attempts
      if (wsReconnectAttempts < maxReconnectAttempts) {
        wsReconnectAttempts++;
        return true;
      }

      wsConnectionState = "error";
      console.error("[WebSocket] Max reconnection attempts reached");
      return false;
    },
    keepAlive: 10000, // Send ping every 10 seconds
    on: {
      connected: (_socket) => {
        wsConnectionState = "connected";
        wsReconnectAttempts = 0;

        // Notify about successful connection
        window.dispatchEvent(new CustomEvent("websocket-connected"));
      },
      connecting: () => {
        wsConnectionState = "connecting";
      },
      closed: (_event) => {
        wsConnectionState = "disconnected";

        // Notify about disconnection
        window.dispatchEvent(new CustomEvent("websocket-disconnected"));
      },
      error: (error) => {
        wsConnectionState = "error";
        console.error("[WebSocket] Connection error:", {
          error,
          code: (error as Error & { code?: number })?.code,
          message: (error as Error)?.message,
          timestamp: DateTime.now().toISO(),
        });

        // Check for authentication errors and let the auth system handle logout
        if (
          (error as Error & { code?: number })?.code === 4403 ||
          (error as Error)?.message?.toLowerCase().includes("unauthorized")
        ) {
          console.warn("[WebSocket] Authentication failed - user will be logged out");
          // The auth error link or auth store will handle the logout
        }
      },
      ping: (received) => {
        if (received) {
        } else {
        }
      },
      pong: (received) => {
        if (received) {
        } else {
        }
      },
    },
  })
);

// Export WebSocket connection state getter
export const getWebSocketConnectionState = () => wsConnectionState;

// Split link for HTTP and WebSocket
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
  },
  wsLink,
  from([tokenRefreshLink, localizedAuthErrorLink, authContextLink, httpLink])
);

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          customers: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          appointments: {
            keyArgs: [
              "salonId",
              "filters",
              ["startDate", "endDate", "statuses", "workerIds", "sortBy", "sortOrder", "first"],
            ],
            merge(existing: ConnectionData, incoming: ConnectionData, { readField }) {
              return mergeConnections(existing, incoming, (merged) => {
                // Sort appointments by startTime in ascending order
                return merged.sort((a, b) => {
                  const startTimeA = readField("startTime", a.node);
                  const startTimeB = readField("startTime", b.node);

                  // Handle missing or invalid startTime - push to end
                  if (!startTimeA || typeof startTimeA !== "string") return 1;
                  if (!startTimeB || typeof startTimeB !== "string") return -1;

                  // Parse ISO strings to DateTime for comparison
                  const dateA = DateTime.fromISO(startTimeA);
                  const dateB = DateTime.fromISO(startTimeB);

                  // Handle invalid dates - push to end
                  if (!dateA.isValid) return 1;
                  if (!dateB.isValid) return -1;

                  // Compare timestamps
                  return dateA.toMillis() - dateB.toMillis();
                });
              });
            },
          },
          incomingAppointments: {
            keyArgs: ["salonId", "filters", ["workerIds"]],
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            merge(existing: IncomingAppointmentConnection, incoming, { args: _args }) {
              if (!existing) {
                return incoming;
              }

              if (existing.edges && incoming.edges) {
                const incomingMap = new Map(incoming.edges.map((edge) => [edge.node.id, edge]));

                const filteredExisting = existing.edges.filter(
                  (edge) => !incomingMap.has(edge.node.id)
                );

                return {
                  ...incoming,
                  edges: [...filteredExisting, ...incoming.edges],
                };
              }

              return incoming;
            },
          },
          todaysAppointments: {
            keyArgs: ["salonId", "filters", ["workerIds"]],
            merge(existing, incoming) {
              if (!existing) {
                return incoming;
              }

              if (existing.edges && incoming.edges) {
                const incomingMap = new Map(incoming.edges.map((edge) => [edge.node.id, edge]));

                const filteredExisting = existing.edges.filter(
                  (edge) => !incomingMap.has(edge.node.id)
                );

                return {
                  ...incoming,
                  edges: [...filteredExisting, ...incoming.edges],
                };
              }

              if (existing.appointments && incoming.appointments) {
                const incomingMap = new Map(
                  incoming.appointments.map((appointment: { id: string }) => [
                    appointment.id,
                    appointment,
                  ])
                );

                const filteredExisting = existing.appointments.filter(
                  (appointment: { id: string }) => !incomingMap.has(appointment.id)
                );

                return {
                  ...incoming,
                  appointments: [...filteredExisting, ...incoming.appointments],
                };
              }

              return incoming;
            },
          },
          workers: {
            keyArgs: ["salonId", "filters"],
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
      fetchPolicy: "cache-and-network",
      // Perf: pause ALL polling (notifications, appointments, workers, …) while
      // the browser tab is hidden/backgrounded, so a dashboard left open in a
      // background tab stops hammering the API. Polling resumes on focus.
      skipPollAttempt: () => typeof document !== "undefined" && document.hidden,
    },
    query: {
      errorPolicy: "all",
      fetchPolicy: "cache-first",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});
